import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const select = "h-11 w-full rounded-md border border-ink-600 bg-ink-800 px-3 text-sm text-slate-100 outline-none focus:ring-2 focus:ring-gold";

/** The company default for when a chauffeur gets their money — overridable on any single trip. */
export const PayoutPolicyCard = () => {
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ pay_on_completion: false, method: "cash" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/company/payout-policy")
      .then(({ data }) => { setState(data); setForm(data.policy); })
      .catch((e) => setError(errMsg(e)));
  }, []);

  const save = async () => {
    setBusy(true); setError(""); setMsg("");
    try {
      const { data } = await api.put("/company/payout-policy", form);
      setState((s) => ({ ...s, policy: data.policy }));
      setMsg("Saved — new trips follow this unless you change them.");
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  if (!state) {
    return (
      <div data-testid="payout-policy-loading" className="rounded-xl border border-ink-600 bg-ink-800/60 p-6">
        {error ? <p data-testid="payout-policy-error" className="text-sm text-red-400">{error}</p>
          : <Loader2 className="h-5 w-5 animate-spin text-gold" />}
      </div>
    );
  }

  return (
    <div data-testid="payout-policy-card" className="rounded-xl border border-ink-600 bg-ink-800/60 p-6">
      <p className="font-mono text-[11px] uppercase tracking-widest text-gold">Paying your chauffeurs</p>
      <p className="mt-2 text-sm text-slate-400">
        Your usual habit. Dispatch can still choose differently on any single trip, and anything paid
        at completion is kept off the next payout run so nobody is paid twice.
      </p>

      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        {[
          { on: true, label: "Pay at trip completion", hint: "marked paid as soon as the trip closes" },
          { on: false, label: "Pay on the payout run", hint: "collected into the monthly statement" },
        ].map((opt) => (
          <button key={String(opt.on)} type="button"
            data-testid={`payout-when-${opt.on ? "completion" : "later"}`}
            onClick={() => setForm((f) => ({ ...f, pay_on_completion: opt.on }))}
            className={`flex-1 rounded-xl border p-4 text-left transition-colors ${
              form.pay_on_completion === opt.on ? "border-gold/60 bg-gold/10" : "border-ink-600 hover:border-gold/30"}`}>
            <span className="block text-sm text-slate-100">{opt.label}</span>
            <span className="block text-xs text-slate-400">{opt.hint}</span>
          </button>
        ))}
      </div>

      <div className="mt-5 max-w-xs">
        <label className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
          How you usually pay
        </label>
        <select data-testid="payout-method" className={select} value={form.method}
          onChange={(e) => setForm((f) => ({ ...f, method: e.target.value }))}>
          {state.methods.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
      </div>

      <Button data-testid="payout-policy-save" disabled={busy} onClick={save}
        className="mt-5 h-11 bg-gold px-6 text-ink-900 hover:bg-gold-light">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save payout default"}
      </Button>
      {msg && <p data-testid="payout-policy-saved" className="mt-3 text-sm text-emerald-400">{msg}</p>}
      {error && <p data-testid="payout-policy-save-error" className="mt-3 text-sm text-red-400">{error}</p>}
    </div>
  );
};
